import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { DateTimePeriod } from '../../entities/form-controls/date-time-period';
import { NxEditCalendarInputValue } from '../../entities/form-controls/nx-edit-calendar-input-value';

interface TimeOption {
    label: string;
    value: string;
  }

@Component({
    selector: 'nx-edit-dateTime-input-v2',
    template: `
    <ng-container [formGroup]="dateTimePeriodForm">
    <div class="nx-edit-dateTime-input-v2">
        <label for="text">{{label}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <div class="flex flex-row" (click)="$event.stopPropagation()">
            <div
                #previewValueDiv
                class="input previewValue flex items-center justify-between"
                (click)="toggleOverlayPanel($event)"
                [ngClass]="{'focus': overlayPanelVisible}">
                <div class="flex-1">
                    <ng-container
                        *ngIf="this.buildDueDateTimeString()">
                        {{this.buildDueDateTimeString()}}
                    </ng-container>
                    <span class="placeholder"
                        *ngIf="!this.buildDueDateTimeString()">
                        {{placeholder}}
                    </span>
                </div>
                <span class="clearIcon" *ngIf="showClearButton && this.buildDueDateTimeString()" (click)="onClearClick($event)">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z" fill="currentColor"></path></svg>
                </span>
                </div>

            <p-overlayPanel
                [dismissable]="dismissable"
                (onShow)="onShow()"
                (onHide)="onHide()"
                #overlayPanel>
                <div class="flex flex-col form nx-edit-dateTime">
                    <div
                        *ngIf="!dateOnly && !(modifier && precision)"
                        class="flex flex-row basis-full">
                        <div class="basis-1/2 flex justify-start">
                            <p-dropdown
                                [style]="{'width':'7.5rem'}"
                                formControlName="modifier"
                                [options]="_modifierOptions"
                                optionLabel="name"
                                [virtualScroll]="false"
                                [filter]="false"
                                [panelStyleClass]="'nx-edit-select'"
                                [placeholder]="'test'"/>
                        </div>
                        <div class="basis-1/2 flex justify-end items-center">
                            <nx-default-button
                                (click)="togglePrecision($event)"
                                [tooltip]="'Zeit hinzufügen'"
                                [style]="isPrecisionDateTime() ? 'accent' : 'dark'"
                                [icon]="'icon-time'">
                            </nx-default-button>
                        </div>
                    </div>
                    <div>
                        <nx-edit-calendar-input
                            class="w-full p-datepicker-inline mb-1"
                            formControlName="calendarFormControl"
                            [selectionMode]="getCalendarSelectionMode()"
                            [showTime]="getShowTime()"
                            [style]="{'width':'270px'}"
                            id="fromDateTime"
                            (blockOverlayDismiss)="onBlockOverlayDismiss()"
                            (unblockOverlayDismiss)="onUnblockOverlayDismiss()"
                            dateFormat="dd.mm.yy">
                        </nx-edit-calendar-input>
                    </div>
                    <div *ngIf="showButtons" class="flex justify-end">
                        <nx-default-button
                            [style]="'light'"
                            class="ml-2"
                            (clicked)="clear()"
                            [label]="'Löschen'">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'accent'"
                            class="ml-2"
                            (clicked)="accept()"
                            [disabled]="!previewDueDateTime"
                            [label]="'Anwenden'">
                        </nx-default-button>
                    </div>
                </div>
            </p-overlayPanel>

        </div>
    </div>
    </ng-container>
  `,
    styles: [`
        .form > *:not(:last-child) {
            margin:0 !important;
        }
        .placeholder {
            color: #888484;
        }
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditDateTimePeriodInputV2Component),
            multi: true
        }
    ]
})
export class EditDateTimePeriodInputV2Component implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit {

    @ViewChild('overlayPanel') overlayPanel: OverlayPanel;
    @ViewChild('previewValueDiv') previewValueDiv: any;
    @Output() blockOverlayDismiss: EventEmitter<any> = new EventEmitter();
    @Output() unblockOverlayDismiss: EventEmitter<any> = new EventEmitter();

    dismissable = true;
    accepted = false;

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() label: string;
    @Input() placeholder: string;
    @Input() modifier: string;
    @Input() precision: string;
    @Input() dateOnly: boolean = false;
    @Input() showButtons: boolean = true;
    @Input() showClearButton: boolean = false;
    @Input() autoToggleOverlay: boolean = false;

    previewDueDateTime: DateTimePeriod;
    overlayPanelVisible: boolean = false;

    private onChange: (value: DateTimePeriod | string | undefined) => void;
    private onTouched: () => void;

    // modifier
    _modifierOptions = [
        { id: 'BEFORE', name: 'vor' },
        { id: 'AFTER', name: 'nach' },
        { id: 'EXACT', name: 'exakt' },
        { id: 'BETWEEN', name: 'zwischen' }
      ];
    modifierFormControlSubscription: Subscription;
    modifierFormControl: FormControl;
    precisionFormControl: FormControl;
    calendarFormControl: FormControl;

    public dateTimePeriodForm: FormGroup;
    public dateTimePeriodFormSubscription: Subscription;

    timeOptions: TimeOption[] = [];

    constructor(
        private _injector: Injector,
        private cd: ChangeDetectorRef,
        public datePipe: DatePipe,
    ) {
        this.timeOptions = this.generateTimeOptions();
    }

    ngAfterViewInit(): void {
        if(this.modifier && this.precision) {
            this.modifierFormControl.setValue(this._modifierOptions.find(option => option.id === this.modifier), { emitEvent: false });
            this.precisionFormControl.setValue(this.precision, { emitEvent: false });
        }
        if(this.autoToggleOverlay) {
            setTimeout(() => { this.toggleOverlayPanel(new MouseEvent('init')); }, 10);
        }
    }

    ngOnDestroy(): void {
        this.modifierFormControlSubscription?.unsubscribe();
        this.dateTimePeriodFormSubscription?.unsubscribe();
    }


    getCalendarSelectionMode(): "single" | "range"{
        return this.modifierFormControl?.value?.id === 'BETWEEN' ? 'range' : 'single';
    }

    getShowTime(): boolean {
        return !this.dateOnly && this.precisionFormControl?.value === 'DATE_TIME';
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
        this.dateTimePeriodForm = new FormGroup({});
        let idFormControl = new FormControl(undefined, []);
        this.dateTimePeriodForm.addControl('id', idFormControl);
        this.modifierFormControl = new FormControl(this._modifierOptions[0], [Validators.required]);
        this.dateTimePeriodForm.addControl('modifier', this.modifierFormControl);
        this.precisionFormControl = new FormControl('DATE', [Validators.required]);
        this.dateTimePeriodForm.addControl('precision', this.precisionFormControl);
        this.calendarFormControl = new FormControl(undefined, [Validators.required]);
        this.dateTimePeriodForm.addControl('calendarFormControl', this.calendarFormControl);
        this.dateTimePeriodFormSubscription = this.dateTimePeriodForm.valueChanges.subscribe( (value) => {
            this.onInputChange();
        });
    }

    onBlockOverlayDismiss() {
        this.overlayPanel.unbindDocumentClickListener();
    }

    onUnblockOverlayDismiss() {
        setTimeout(() => {
            this.overlayPanel.bindDocumentClickListener();
        }, 1);
    }

    onInputChange(){
        this.accepted = false;
        let value: DateTimePeriod = undefined;
        let dateTimeModifierValue = this.dateTimePeriodForm.get('modifier')?.value;
        if(dateTimeModifierValue != null && dateTimeModifierValue.id === 'BETWEEN') {
            let calendarValue = this.dateTimePeriodForm.get('calendarFormControl')?.value as NxEditCalendarInputValue;
            if(calendarValue && calendarValue.fromDateTime && calendarValue.toDateTime) {
                value = new DateTimePeriod();
                value.id = this.dateTimePeriodForm.get('id')?.value;
                value.fromDateTime = calendarValue.fromDateTime;
                value.toDateTime = calendarValue.toDateTime;
                value.modifier = dateTimeModifierValue.id;
                // we use the acutal precision the user really has entered and not only selected
                value.precision = calendarValue.precision;
            }
        } else if (dateTimeModifierValue!= null) {
            // every other modifier
            let calendarValue = this.dateTimePeriodForm.get('calendarFormControl')?.value as NxEditCalendarInputValue;
            if(calendarValue && calendarValue.dateTime) {
                value = new DateTimePeriod();
                value.id = this.dateTimePeriodForm.get('id')?.value;
                value.dateTime = calendarValue.dateTime;
                value.modifier = dateTimeModifierValue.id;
                // we use the acutal precision the user really has entered and not only selected
                value.precision = calendarValue.precision;
            }
        }
        this.previewDueDateTime = value;
        if(!this.showButtons) {
            this.accept()
        }
    }

    accept() {
        this.accepted = true;
        if(this.onChange) {
            this.onChange(this.buildValue());
        }
        this.overlayPanel?.hide();
    }

    buildValue(): DateTimePeriod | string | undefined {
        if(!this.dateOnly) {
            return this.previewDueDateTime;
        } else {
            return this.previewDueDateTime?.dateTime
        }
    }

    clear() {
        this.previewDueDateTime = undefined;
        this.dateTimePeriodForm?.reset();
        // Wenn modifier/precision als @Input fixiert sind, auf diesen Wert zurücksetzen statt auf den Default
        const defaultModifier = this.modifier
            ? this._modifierOptions.find(o => o.id === this.modifier) ?? this._modifierOptions[0]
            : this._modifierOptions[0];
        this.modifierFormControl.setValue(defaultModifier, { emitEvent: false });
        const defaultPrecision = this.precision ?? 'DATE';
        this.precisionFormControl.setValue(defaultPrecision, { emitEvent: false });
        this.onChange(undefined);
    }

    togglePrecision($event: MouseEvent) {
        if(this.precisionFormControl?.value === 'DATE') {
            this.precisionFormControl?.setValue('DATE_TIME');
        } else {
            this.precisionFormControl?.setValue('DATE');
        }
    }

    writeValue(dateTimePeriod: any): void {
        if(dateTimePeriod && typeof dateTimePeriod === 'object') {
            // we cannot use patch value for the modifier, because the incoming value is not the
            // object we need to push into the multi select and the modifier must be set
            // before the other values, because, depending on the modifier, other form fields
            // are needed
            if(dateTimePeriod.id) {
                this.dateTimePeriodForm.get('id')?.setValue(dateTimePeriod.id);
            }
            if(dateTimePeriod.modifier) {
                this.dateTimePeriodForm.get('modifier')?.setValue(this._modifierOptions.find(option => option.id === dateTimePeriod.modifier));
            }
            if(dateTimePeriod.precision) {
                this.dateTimePeriodForm.get('precision')?.setValue(dateTimePeriod.precision);
            }
            if(dateTimePeriod.precision &&
                (dateTimePeriod.dateTime || (dateTimePeriod.fromDateTime && dateTimePeriod.toDateTime))) {
                    let calendarValue : NxEditCalendarInputValue = {
                        dateTime: dateTimePeriod.dateTime,
                        fromDateTime: dateTimePeriod.fromDateTime,
                        toDateTime: dateTimePeriod.toDateTime,
                        precision: dateTimePeriod.precision
                    }
                this.dateTimePeriodForm.get('calendarFormControl')?.setValue(calendarValue);
                }
        } else if (dateTimePeriod && typeof dateTimePeriod === 'string' && this.dateOnly) {
            this.dateTimePeriodForm.get('modifier')?.setValue('EXACT');
            this.dateTimePeriodForm.get('precision')?.setValue('DATE_TIME');
            let calendarValue : NxEditCalendarInputValue = {
                dateTime: dateTimePeriod,
                precision: 'DATE_TIME'
            }
            this.dateTimePeriodForm.get('calendarFormControl')?.setValue(calendarValue);

        }
    }

    registerOnChange(fn: (value: DateTimePeriod | string | undefined) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Optional: Implementieren Sie die Logik zum Deaktivieren des Steuerelements
        this.isDisabled = isDisabled;
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    isBetween(): boolean {
        return false;
    }

    //____
    toggleOverlayPanel($event: MouseEvent) {
        this.overlayPanel.toggle($event, this.previewValueDiv.nativeElement)
    }

    onClearClick($event: MouseEvent) {
        $event.stopPropagation();
        this.clear();
    }

    isPrecisionDateTime(): boolean {
        return this.dateTimePeriodForm?.get('precision')?.value === 'DATE_TIME';
    }

    generateTimeOptions(): TimeOption[] {
        const options: TimeOption[] = [];

        for (let hour = 0; hour < 24; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const hourStr = hour.toString().padStart(2, '0');
            const minuteStr = minute.toString().padStart(2, '0');
            const timeStr = `${hourStr}:${minuteStr}`;

            options.push({
              label: timeStr,
              value: timeStr
            });
          }
        }

        return options;
    }

    // TODO: this should be moved into a service - but: Currently dateTimePeriod is implemented in both: core and frontend-web
    public buildDueDateTimeString() {
        if (this.previewDueDateTime) {
            if(!this.dateOnly){
                if(this.previewDueDateTime.modifier === 'BETWEEN') {
                    if(this.previewDueDateTime.precision === 'DATE_TIME') {
                        return this.datePipe.transform(this.previewDueDateTime.fromDateTime, "EEE, dd.MM.yy HH:mm") + ' - ' + this.datePipe.transform(this.previewDueDateTime.toDateTime, "EEE, dd.MM.yy HH:mm");
                    } else {
                        return this.datePipe.transform(this.previewDueDateTime.fromDateTime, "EEE, dd.MM.yy") + ' - ' + this.datePipe.transform(this.previewDueDateTime.toDateTime, "EEE, dd.MM.yy");
                    }
                } else {
                    let modifierString: string = '';
                    switch(this.previewDueDateTime.modifier) {
                        case 'BEFORE':
                            modifierString = 'vor ';
                            break;
                        case 'AFTER':
                            modifierString = 'nach ';
                            break;
                    }
                    if(this.previewDueDateTime.precision === 'DATE_TIME') {
                        return this.datePipe.transform(this.previewDueDateTime.dateTime, "EEE, dd.MM.yy") + ' ' + modifierString + this.datePipe.transform(this.previewDueDateTime.dateTime, "HH:mm");
                    } else {
                        return modifierString + this.datePipe.transform(this.previewDueDateTime.dateTime, "EEE, dd.MM.yy");
                    }
                }
            } else {
                // date only
                return this.datePipe.transform(this.previewDueDateTime.dateTime, "dd.MM.yy")
            }
        }
         return undefined;
      }

    onShow(){
        this.overlayPanelVisible = true
        this.blockOverlayDismiss.emit();
    }

    onHide(){
        this.overlayPanelVisible = false;
        if(!this.accepted) {
            console.log(this.dateTimePeriodForm);
            this.clear();
        }
        this.unblockOverlayDismiss.emit();

    }
}
